(() => {
  if (customElements.get('pyp-photo-commerce')) return;
  class PypPhotoCommerce extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.closest('form');
      this.upload = this.closest('pyp-photo-upload');
      if (!this.form || !this.upload) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.retry = this.upload.querySelector('[data-pyp-photo-retry]');
      this.recovery = this.upload.querySelector('[data-pyp-photo-recovery]');
      this.retry?.addEventListener('click', () => this.submit(), { signal });
      this.approval = this.form.querySelector('[data-pyp-approve-input]');
      if (this.approval && !this.initialized) { this.approval.disabled = false; this.approval.checked = false; }
      this.initialized = true;
      this.variantSelect = this.form.querySelector('[data-pyp-variant-select]');
      // Bubble after the variant control updates its base price and availability.
      this.form.addEventListener('change', event => {
        if (event.target === this.approval || event.target === this.variantSelect) this.syncPurchasePrices();
        if (event.target === this.approval) this.syncAddedSelection();
      }, { signal });
      this.syncPurchasePrices();
      this.form.addEventListener('submit', event => {
        // Stop the delegated host AJAX handler: one form, one request, no file URLs in logs.
        event.preventDefault(); event.stopImmediatePropagation(); this.submit();
      }, { signal });
      ['pyp:photo-reset', 'pyp:variant-change', 'pyp:orientation-change', 'pyp:crop-confirmed'].forEach(type => {
        this.form.addEventListener(type, () => this.syncAddedSelection(), { signal });
      });
      this.setAttribute('data-ready', '');
    }

    syncPurchasePrices() {
      const option = this.variantSelect?.selectedOptions[0];
      if (!option?.dataset.price) return;
      const basePrice = option.dataset.price;
      const price = this.approval?.checked && this.approval.dataset.variantId
        ? option.dataset.priceWithApproval || basePrice : basePrice;
      // Only the purchase amounts include the service; the product price stays native.
      this.closest('product-info')?.querySelectorAll('.productcard__item-buy [id^="dynamic-price-"], .totalCardBar__item-buy [id^="dynamic-price-"]')
        .forEach(label => { label.textContent = price; });
    }

    selectionMatches(selection) {
      return Boolean(selection && selection.photo === this.upload.ready
        && selection.variantId === this.variantSelect?.value
        && selection.approvalId === (this.approval?.checked ? this.approval.dataset.variantId : null));
    }

    syncAddedSelection() {
      if (this.pending || this.uncertain) return;
      this.added = this.selectionMatches(this.confirmedSelection);
      if (!this.added) delete this.upload.dataset.pypCartVerified;
    }

    payload(photo, variantId, requestId) {
      const data = new FormData();
      const nested = Boolean(this.approval?.checked && this.approval.dataset.variantId);
      const field = name => nested ? 'items[0][' + name + ']' : name;
      const property = name => nested ? 'items[0][properties][' + name + ']' : 'properties[' + name + ']';
      data.set(field('id'), variantId);
      data.set(field('quantity'), '1');
      data.set(property('Photo'), photo.file, photo.file.name);
      data.set(property('Canvas size'), photo.size.width + ' × ' + photo.size.height + ' cm');
      data.set(property('Orientation'), photo.size.orientation === 'horizontal' ? 'Horizontal' : 'Vertical');
      data.set(property('_Crop'), JSON.stringify(photo.crop));
      data.set(property('_PYP request'), requestId);
      if (nested) {
        data.set(property('Pre-approval'), 'Requested');
        data.set('items[1][id]', this.approval.dataset.variantId);
        data.set('items[1][quantity]', '1');
        data.set('items[1][parent_id]', variantId);
        data.set('items[1][properties][_PYP request]', requestId);
      }
      return data;
    }

    async submit() {
      if (this.pending || !this.isConnected) return;
      this.setPending(true);
      try {
      const lifecycle = this.controller;
      let cart;
      if (this.added && this.confirmedAttempt) {
        cart = await this.readCart();
        if (!this.isConnected || this.controller !== lifecycle) return;
        const state = this.cartState(cart, ...this.confirmedAttempt);
        if (state === 'unknown') {
          this.upload.showError('We could not check your cart. Please check your connection and try again.', false); return;
        }
        // A customer may remove only the service. Do not duplicate its surviving painting.
        if (state !== 'absent') { this.openCart(); return; }
        this.added = false; this.confirmedAttempt = null; this.confirmedSelection = null;
        delete this.upload.dataset.pypCartVerified;
      }
      // A retry is always user initiated. Check the previous attempt before any new POST.
      // Keep this identity even if the user changes their photo/options after an error.
      if (this.attempt) {
        this.upload.clearError(); this.upload.setStatus('Checking your previous upload…');
        const state = await this.reconcile(...this.attempt);
        if (state === 'confirmed') { await this.complete(); return; }
        if (state !== 'absent') { this.showRecovery(state); return; }
      }
      if (!this.isConnected || this.controller !== lifecycle) return;
      const photo = this.upload.ready;
      const select = this.form.querySelector('[data-pyp-variant-select]');
      const option = select?.selectedOptions[0];
      if (!photo || !this.upload.sameSize(photo.size, this.upload.selectedSize())) {
        this.upload.showError(this.upload.requirement()); this.upload.focusUpload(); return;
      }
      if (!option || option.disabled || option.dataset.available !== 'true') {
        this.upload.showError('This option is unavailable. Please select another option.'); return;
      }
      if (this.approval?.checked && (!this.approval.dataset.variantId || !option.dataset.priceWithApproval)) {
        this.upload.showError('Pre-approval pricing is unavailable. Please refresh this page before adding it.', false); return;
      }
      // A different tab can change the cart currency after this page rendered its prices.
      this.upload.clearError(); this.upload.setStatus('Checking your cart…');
      cart = cart || await this.readCart();
      if (!this.isConnected || this.controller !== lifecycle) return;
      this.upload.setStatus('');
      if (!cart || !cart.currency || !this.dataset.currency) {
        this.upload.showError('We could not confirm your cart currency. Please check your connection and try again.', false); return;
      }
      if (cart.currency !== this.dataset.currency) {
        this.upload.showError('Your shopping currency has changed. Refresh this page to see current prices, then select your photo again. Nothing new has been added.', false); return;
      }
      const requestId = crypto.randomUUID();
      const variantId = select.value;
      const approvalId = this.approval?.checked ? this.approval.dataset.variantId : null;
      const body = this.payload(photo, variantId, requestId);
      this.submittedSelection = { photo, variantId, approvalId };
      this.attempt = [requestId, variantId, approvalId];
      this.uncertain = false;
      if (this.recovery) this.recovery.hidden = true;
      this.upload.clearError(); this.upload.setStatus('Uploading your photo. Please keep this page open…');
      this.beginCartAddition(requestId);
      const cartHost = this.cartAddition?.cart || document.querySelector('cart-drawer.myart-cart-host');
      const upload = async () => {
        if (!this.isConnected || this.controller !== lifecycle) return 'absent';
        cartHost?.assertCartReady?.();
        try {
          const response = await fetch(this.dataset.addUrl + '.js', {
            method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' },
            signal: AbortSignal.timeout(60000)
          });
          if (!response.ok) throw new Error('cart-not-confirmed');
          const result = await response.json();
          const lines = Array.isArray(result.items) ? result.items : [result];
          if (!this.verify(lines, requestId, variantId, approvalId)) throw new Error('attachment-not-confirmed');
          return 'confirmed';
        } catch {
          // Keep the mutation lock until an ambiguous upload has been checked.
          const state = await this.reconcile(requestId, variantId, approvalId);
          // A queued checkout must see this gate before the upload releases its lock.
          if (state === 'unknown' || state === 'partial') this.markCartAdditionUncertain();
          return state;
        }
      };
      let state;
      try {
        // Checkout and cart edits must wait for this external file upload too.
        state = typeof cartHost?.withCartLock === 'function' ? await cartHost.withCartLock(upload) : await upload();
      } catch (error) {
        if (error?.cartReviewRequired) {
          // The shared guard stopped this unsent attempt; preserve the selected photo.
          this.attempt = null; this.submittedSelection = null; this.cancelCartAddition();
          this.upload.showError(error.message, false); return;
        }
        // Lock acquisition can time out without a POST. Never automatically retry.
        state = await this.reconcile(requestId, variantId, approvalId);
      }
      // Cart presentation may acquire the same lock; run it only after release.
      if (state === 'confirmed') await this.complete();
      else this.showRecovery(state);
      } finally { this.setPending(false); }
    }

    async complete() {
      this.confirmedAttempt = this.attempt;
      this.confirmedSelection = this.submittedSelection;
      this.uncertain = false; this.attempt = null;
      this.added = this.selectionMatches(this.confirmedSelection);
      if (this.added) this.upload.dataset.pypCartVerified = 'true';
      else delete this.upload.dataset.pypCartVerified;
      this.upload.clearError(); this.upload.setStatus('');
      if (this.recovery) this.recovery.hidden = true;
      const addition = this.cartAddition;
      this.cartAddition = null;
      if (addition) {
        try {
          const result = await addition.cart.confirmExternalAdd(addition.token);
          if (result?.ok) return;
        } catch { /* An optional cart presentation failure must never retry a confirmed upload. */ }
      }
      this.openCart();
    }

    beginCartAddition(requestId) {
      this.cancelCartAddition();
      try {
        const cart = document.querySelector('cart-drawer.myart-cart-host');
        if (typeof cart?.beginExternalAdd !== 'function' || typeof cart.confirmExternalAdd !== 'function') return;
        const token = cart.beginExternalAdd(requestId);
        if (token) this.cartAddition = { cart, token };
      } catch { /* Older hosts retain their existing cart entry point. */ }
    }

    cancelCartAddition() {
      const addition = this.cartAddition;
      this.cartAddition = null;
      try { addition?.cart.cancelExternalAdd?.(addition.token); } catch { /* Presentation only. */ }
    }

    markCartAdditionUncertain() {
      const addition = this.cartAddition;
      try { addition?.cart.markExternalAddUncertain?.(addition.token); } catch { /* Never retry a file POST when a host hook fails. */ }
    }

    showRecovery(state) {
      this.cancelCartAddition();
      this.uncertain = true;
      const messages = {
        absent: 'Your painting was not found in the cart. Your photo is still here. Choose Retry upload to try again.',
        partial: 'Part of your painting was added, but its photo or pre-approval could not be confirmed. Open your cart and remove that painting and its pre-approval before retrying.',
        unknown: 'We could not check your upload. Keep this page open and check your internet connection, then choose Check upload.'
      };
      this.upload.showError(messages[state] || messages.unknown, false);
      if (this.retry) this.retry.textContent = state === 'absent' ? 'Retry upload' : 'Check upload';
      if (this.recovery) {
        this.recovery.hidden = false;
        this.recovery.parentElement.append(this.recovery);
      }
    }

    verify(lines, requestId, variantId, approvalId) {
      const main = lines.find(line => String(line.variant_id || line.id) === variantId && line.properties?.['_PYP request'] === requestId);
      const isPhotoLink = value => typeof value === 'string' && /^(https:\/\/|\/\/)/.test(value);
      if (!main || !isPhotoLink(main.properties?.Photo)) return false;
      if (!approvalId) return true;
      return lines.some(line => String(line.variant_id || line.id) === approvalId && line.properties?.['_PYP request'] === requestId
        && line.parent_relationship?.parent_key === main.key);
    }

    async readCart() {
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!response.ok) return null;
        const cart = await response.json();
        if (!Array.isArray(cart?.items) || cart.items.some(line => !line || typeof line !== 'object')) return null;
        return cart;
      } catch { return null; }
    }

    cartState(cart, requestId, variantId, approvalId) {
      try {
        if (!cart) return 'unknown';
        if (this.verify(cart.items, requestId, variantId, approvalId)) return 'confirmed';
        return cart.items.some(line => line.properties?.['_PYP request'] === requestId) ? 'partial' : 'absent';
      } catch { return 'unknown'; }
    }

    async reconcile(requestId, variantId, approvalId) {
      return this.cartState(await this.readCart(), requestId, variantId, approvalId);
    }

    setPending(pending) {
      this.pending = pending;
      if (pending) {
        this.formBusy = this.form.getAttribute('aria-busy');
        this.form.dataset.cartSubmitPending = 'true'; this.form.setAttribute('aria-busy', 'true');
        const root = this.closest('.pyp-product-card-section') || this.form;
        this.pendingButtons = Array.from(root.querySelectorAll('.productcard__item-buy, .totalCardBar__item-buy')).map(button => ({
          button,
          disabled: button.disabled,
          busy: button.getAttribute('aria-busy'),
          ariaDisabled: button.getAttribute('aria-disabled'),
          loading: button.classList.contains('loading'),
        }));
        this.disabledControls = Array.from(this.form.querySelectorAll('input, select, button')).map(control => [control, control.disabled]);
        this.disabledControls.forEach(([control]) => { control.disabled = true; });
        this.pendingButtons.forEach(({ button }) => {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        });
      } else {
        delete this.form.dataset.cartSubmitPending;
        if (this.formBusy === null) this.form.removeAttribute('aria-busy');
        else this.form.setAttribute('aria-busy', this.formBusy);
        this.disabledControls?.forEach(([control, disabled]) => { control.disabled = disabled; });
        this.pendingButtons?.forEach(({ button, disabled, busy, ariaDisabled, loading }) => {
          button.disabled = disabled;
          if (busy === null) button.removeAttribute('aria-busy');
          else button.setAttribute('aria-busy', busy);
          if (ariaDisabled === null) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', ariaDisabled);
          button.classList.toggle('loading', loading);
        });
        this.disabledControls = null;
        this.pendingButtons = null;
        this.formBusy = null;
      }
    }

    openCart() {
      if (!this.isConnected) return;
      // Reuse the host's established cart entry point; do not fork its implementation.
      const cartLink = document.querySelector('.header__icon--cart');
      if (cartLink) cartLink.click();
      else window.location.assign(this.dataset.cartUrl);
    }
    disconnectedCallback() {
      // An in-flight file write still needs its issued token to report uncertainty.
      if (!this.pending) this.cancelCartAddition();
      this.controller?.abort(); this.controller = null; this.removeAttribute('data-ready');
    }
  }
  customElements.define('pyp-photo-commerce', PypPhotoCommerce);
})();
